import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { money } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import { Loader2 } from "lucide-react";

export default function AgentEarnings() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/agent/earnings").then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  const m = (v) => money(v, data.currency, data.symbol);

  return (
    <div className="space-y-4">
      <h1 className="font-display text-2xl font-bold tracking-tight">Earnings history</h1>
      {data.months.length === 0 ? (
        <p className="text-sm text-muted-foreground py-12 text-center">No earnings yet.</p>
      ) : (
        <Accordion type="single" collapsible className="space-y-3">
          {data.months.map((mo) => (
            <Card key={mo.month} className="overflow-hidden">
              <AccordionItem value={mo.month} className="border-0">
                <div className="px-4 pt-3 flex justify-end gap-3 text-xs">
                  <Link to={`/statement/me?month=${mo.month}`} className="text-primary hover:underline" data-testid={`statement-link-${mo.month}`}>Statement (PDF)</Link>
                </div>
                <AccordionTrigger className="px-4 hover:no-underline" data-testid={`earning-month-${mo.month}`}>
                  <div className="flex items-center justify-between w-full pr-3">
                    <div className="text-left">
                      <p className="font-display font-bold text-base">{monthName(mo.month)}</p>
                      <p className="text-xs text-muted-foreground">{mo.bags_sold} bags</p>
                    </div>
                    <div className="text-right">
                      <p className="font-display font-bold">{m(mo.total)}</p>
                      <Badge variant={mo.status === "paid" ? "default" : "secondary"} className="capitalize mt-1 text-[10px]">
                        {mo.status}
                      </Badge>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <div className="space-y-2 text-sm border-t border-border pt-3">
                    <Row label="Base commission" value={m(mo.base_commission)} />
                    <Row label="Accelerator bonus" value={m(mo.accelerator_bonus)} />
                    <Row label="Above-price bonus" value={m(mo.above_price_bonus)} />
                    <Row label="New-customer bonus" value={m(mo.new_customer_bonus)} />
                    <Row label="Star-agent bonus" value={m(mo.star_bonus)} />
                    {mo.deductions > 0 && <Row label="Deductions" value={`−${m(mo.deductions)}`} danger />}
                    <div className="flex justify-between font-semibold pt-2 border-t border-border">
                      <span>Total</span><span>{m(mo.total)}</span>
                    </div>
                    {mo.payout_date && (
                      <p className="text-xs text-muted-foreground">Paid on {new Date(mo.payout_date).toLocaleDateString()}</p>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Card>
          ))}
        </Accordion>
      )}
    </div>
  );
}

function Row({ label, value, danger }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className={danger ? "text-destructive" : ""}>{value}</span>
    </div>
  );
}
function monthName(m) {
  const [y, mo] = m.split("-");
  return new Date(y, mo - 1).toLocaleString(undefined, { month: "long", year: "numeric" });
}
